import { useState, useCallback, useEffect, useRef, memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Play, CircleNotch, Clock, CheckCircle } from "@phosphor-icons/react";
import BASE_URL from "Utilities/BASE_URL";
import { formatDuration } from "Utilities/formatDuration";
import { getPlaceholderGradient } from "Utilities/placeholderColor";

const SERVER_URL = BASE_URL.replace(/\/api$/, "");

// Single-trailer coordinator — only one hover-preview iframe may play at a
// time across the whole page (Netflix/YouTube TV pattern). Each card that
// starts previewing first tells every other card to stop.
const TRAILER_BUS = (() => {
  if (typeof window === "undefined") return null;
  return {
    current: null,
    listeners: new Set(),
    acquire(id, stop) {
      if (this.current && this.current.id !== id) {
        try {
          this.current.stop();
        } catch {}
      }
      this.current = { id, stop };
    },
    release(id) {
      if (this.current?.id === id) this.current = null;
    },
  };
})();

// `(hover: hover) and (pointer: fine)` isolates desktop — on touch devices
// the iframe costs far more than any preview benefit.
const hasFineHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

const getCourseThumbnail = (course) => {
  if (course?.trailer?.assets?.thumbnail)
    return course.trailer.assets.thumbnail;
  if (course?.thumbnail) return course.thumbnail;
  if (course?.image) return `${SERVER_URL}/${course.image}`;
  if (course?.mobileImage) return `${SERVER_URL}/${course.mobileImage}`;
  return null;
};

const getImageUrl = (path) =>
  !path || path.startsWith("http") ? path : `${SERVER_URL}/${path}`;

const getTrailerId = (course) => course?.trailer?.videoId || null;

const getLessonInfo = (course, t) => {
  if (course.course_type === "series") {
    const count = course.series?.length || 0;
    return count > 0
      ? `${count} ${t("course_card.episodes", "Episodes")}`
      : null;
  }
  if (course.course_type === "playlist") {
    const count =
      course.chapters?.reduce(
        (sum, ch) => sum + (ch.lessons?.length || 0),
        0,
      ) || 0;
    return count > 0 ? `${count} ${t("course_card.lessons", "Lessons")}` : null;
  }
  return t("course_card.masterclass", "Masterclass");
};

let cardIdSeq = 0;
const nextCardId = () => ++cardIdSeq;

const CourseCard = ({ course, progress = null, priority = false }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const thumbnail = getCourseThumbnail(course);
  const instructor = course.instructor;
  const profileImg = instructor?.profileImage
    ? getImageUrl(instructor.profileImage)
    : null;
  const lessonInfo = getLessonInfo(course, t);
  const trailerId = getTrailerId(course);
  const duration = formatDuration(course?.totalDuration);

  const hasProgress =
    progress &&
    typeof progress.percent === "number" &&
    progress.percent > 0;
  const showLessonPill =
    progress &&
    typeof progress.lessonsCompleted === "number" &&
    typeof progress.totalLessons === "number" &&
    progress.totalLessons > 0;
  const isCompleted = progress?.isCompleted === true;
  const isRTL = i18n.language === "ar";

  const cardIdRef = useRef(null);
  if (cardIdRef.current === null) cardIdRef.current = nextCardId();

  const [hovering, setHovering] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const hoveringRef = useRef(false);
  const hoverTimerRef = useRef(null);

  const stopPreview = useCallback(() => {
    hoveringRef.current = false;
    setHovering(false);
    setVideoReady(false);
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Prefetch course detail on hover — clicking the card becomes instant.
    if (course?.slug) {
      const endpoint = `courses/${course.slug}`;
      queryClient.prefetchQuery({
        queryKey: [endpoint, i18n.language],
        queryFn: async () => {
          const res = await fetch(
            `${BASE_URL}/${i18n.language}/${endpoint}`,
            { headers: { "Content-Type": "application/json" } }
          );
          if (!res.ok) throw new Error("prefetch failed");
          return res.json();
        },
        staleTime: 5 * 60 * 1000,
      });
    }

    if (!trailerId || !hasFineHover()) return;
    // 3s hover-intent — trailer plays only after sustained focus.
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      hoveringRef.current = true;
      TRAILER_BUS?.acquire(cardIdRef.current, stopPreview);
      setHovering(true);
    }, 3000);
  }, [trailerId, course?.slug, i18n.language, queryClient, stopPreview]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    TRAILER_BUS?.release(cardIdRef.current);
    stopPreview();
  }, [stopPreview]);

  useEffect(
    () => () => {
      clearTimeout(hoverTimerRef.current);
      TRAILER_BUS?.release(cardIdRef.current);
    },
    []
  );

  // Listen for api.video postMessage events to detect when video starts.
  useEffect(() => {
    if (!hovering || !trailerId) return;
    const handleMessage = (e) => {
      if (!hoveringRef.current) return;
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (
          data?.type === "playing" ||
          data?.type === "play" ||
          data?.name === "playing" ||
          data?.name === "play"
        ) {
          setVideoReady(true);
        }
      } catch {}
    };
    window.addEventListener("message", handleMessage);
    const fallback = setTimeout(() => {
      if (hoveringRef.current) setVideoReady(true);
    }, 2000);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(fallback);
    };
  }, [hovering, trailerId]);

  return (
    <Link
      to={`/course/${course.slug}`}
      className="group relative block"
      style={{ contain: "content" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative rounded-xl overflow-hidden transition-shadow duration-300 group-hover:shadow-[0_0_30px_rgba(255,255,255,0.25)]"
        style={{ background: getPlaceholderGradient(course?._id || course?.slug) }}
      >
        {trailerId && hovering && (
          <iframe
            src={`https://embed.api.video/vod/${trailerId}?autoplay=true&muted=false&loop=true&hideTitle=true&hidePoster=true`}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78%] h-full"
            style={{ border: "none", minHeight: "100%", minWidth: "100%" }}
            allow="autoplay; fullscreen"
            loading="lazy"
          />
        )}

        {/* Thumbnail — no opacity fade-in. Browsers paint decoded images
            instantly; an opacity tween just added a visible "flash" every
            time the card re-rendered. Scroll-back to a cached image should
            feel instant, not animated. */}
        {thumbnail && (
          <img
            src={thumbnail}
            width={520}
            height={280}
            sizes="(min-width: 1024px) 300px, 260px"
            className={`relative w-full h-[200px] lg:h-[280px] object-cover ${
              videoReady ? "opacity-0" : "opacity-100"
            } ${!hovering ? "group-hover:scale-105 transition-transform" : ""}`}
            style={{ zIndex: videoReady ? 0 : 2 }}
            alt={course.name}
            decoding="async"
            loading="eager"
            fetchpriority={priority ? "high" : "low"}
          />
        )}

        {hovering && !videoReady && (
          <div className="absolute inset-0 z-[3] flex items-center justify-center">
            <CircleNotch size={36} className="text-white animate-spin" />
          </div>
        )}

        <div
          className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
          style={{ zIndex: 3, pointerEvents: "none" }}
        />

        {course?.tags?.length > 0 && (
          <div
            className={`absolute top-3 ${
              i18n.language === "ar" ? "left-3" : "right-3"
            } flex flex-wrap gap-1 z-10`}
          >
            {course.tags.map((tag, i) => (
              <span
                key={i}
                className="px-2 py-0.5 text-xs font-medium rounded-md shadow-md capitalize"
                style={{ backgroundColor: tag.color, color: tag.text_color }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {!trailerId && (
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center z-[4]">
            <div className="w-14 h-14 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/25 scale-75 group-hover:scale-100 transition-transform duration-300">
              <Play size={26} weight="fill" className="text-white ms-0.5" />
            </div>
          </div>
        )}

        <div
          className={`absolute top-3 ${isRTL ? "right-3" : "left-3"} z-10 flex items-center gap-1`}
        >
          {isCompleted ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-green-500/90 px-2.5 py-1 rounded-full shadow-md">
              <CheckCircle size={12} weight="fill" />
              {t("course_card.completed", "Completed")}
            </span>
          ) : showLessonPill ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-black/75 border border-primary/40 px-2.5 py-1 rounded-md shadow-md">
              <CheckCircle size={12} weight="fill" className="text-primary" />
              {progress.lessonsCompleted}/{progress.totalLessons}
            </span>
          ) : lessonInfo ? (
            <span className="text-[11px] font-medium text-white/90 bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/10">
              {lessonInfo}
            </span>
          ) : null}
        </div>

        {duration && (
          <div
            className={`absolute bottom-3 ${isRTL ? "left-3" : "right-3"} z-10`}
          >
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-white/90 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md border border-white/10">
              <Clock size={12} />
              {duration}
            </span>
          </div>
        )}

        {hasProgress && !isCompleted && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15 z-10">
            <div
              className="h-full bg-primary"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        )}
      </div>

      <div className="mt-3">
        <h3 className="text-lg font-semibold text-white line-clamp-1 group-hover:text-primary transition-colors duration-300">
          {course.name}
        </h3>
        {instructor?.name && (
          <div className="flex items-center gap-2 mt-1.5">
            {profileImg ? (
              <img
                src={profileImg}
                alt={instructor.name}
                className="w-6 h-6 rounded-full object-cover ring-1 ring-white/20"
                width={24}
                height={24}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary/30 flex items-center justify-center text-[10px] font-bold text-white ring-1 ring-white/20">
                {instructor.name.charAt(0)}
              </div>
            )}
            <span className="text-sm text-white/50">{instructor.name}</span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default memo(CourseCard, (prev, next) => {
  if (prev.course?._id !== next.course?._id) return false;
  if (prev.course?.slug !== next.course?.slug) return false;
  if (prev.progress?.percent !== next.progress?.percent) return false;
  if (prev.progress?.isCompleted !== next.progress?.isCompleted) return false;
  if (prev.progress?.lessonsCompleted !== next.progress?.lessonsCompleted)
    return false;
  if (prev.priority !== next.priority) return false;
  return true;
});
